import { useMemo, useState, type ReactNode } from 'react';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import type { CMDBDeviceCrosswalkResponse, CMDBDeviceCrosswalkRow, IntegrationConnectionCMDBWritebackResult } from '@app/types/sdk';
import { formatConfidenceBadgeLabel, getConfidenceBadgeClass } from '@app/utils/confidence';

export type CrosswalkFilter = 'all' | 'needs_review' | 'probable' | 'duplicate' | 'reap_only' | 'cmdb_only' | 'matched';

type ReviewInput = {
  findingId: string;
  expectedVersion: number;
  action: string;
  reapEntityId?: string;
  assignedTo?: string;
  reason: string;
};

type Props = {
  data: CMDBDeviceCrosswalkResponse | null;
  loading: boolean;
  onRefresh: () => void;
  onReview: (input: ReviewInput) => Promise<void>;
  canCreateCI?: boolean;
  onCreateCI?: (input: { deviceId: string; operationId: string }) => Promise<IntegrationConnectionCMDBWritebackResult>;
  filter?: CrosswalkFilter;
  onFilterChange?: (filter: CrosswalkFilter) => void;
};

type CrosswalkGroup = {
  id: string;
  status: string;
  rows: CMDBDeviceCrosswalkRow[];
};

type TieSummary = {
  tied: boolean;
  count: number;
  score: number | null;
};

const filterLabels: Array<[CrosswalkFilter, string]> = [
  ['all', 'All'],
  ['needs_review', 'Needs review'],
  ['probable', 'Probable'],
  ['duplicate', 'Duplicate warnings'],
  ['reap_only', 'REAP only'],
  ['cmdb_only', 'CMDB only'],
  ['matched', 'Matched']
];

const CMDBDeviceCrosswalk = ({ data, loading, onRefresh, onReview, canCreateCI = false, onCreateCI, filter, onFilterChange }: Props) => {
  const [localFilter, setLocalFilter] = useState<CrosswalkFilter>('all');
  const activeFilter = filter ?? localFilter;
  const changeFilter = onFilterChange ?? setLocalFilter;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [candidateIds, setCandidateIds] = useState<Record<string, string>>({});
  const [assignedTo, setAssignedTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createRequest, setCreateRequest] = useState<{ deviceId: string; deviceName: string; operationId: string } | null>(null);
  const [creatingCI, setCreatingCI] = useState(false);
  const [createResults, setCreateResults] = useState<Record<string, IntegrationConnectionCMDBWritebackResult>>({});
  const [latestCreateResult, setLatestCreateResult] = useState<IntegrationConnectionCMDBWritebackResult | null>(null);

  const groups = useMemo(() => groupCrosswalkRows(data?.items ?? []).filter((group) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'needs_review') return group.rows.some((row) => row.requiresDecision);
    return group.status === activeFilter;
  }), [activeFilter, data?.items]);

  const submit = async (row: CMDBDeviceCrosswalkRow, action: string) => {
    if (!row.identityFindingId || row.identityFindingVersion == null || !reason.trim()) return;
    const candidateId = candidateIds[row.identityFindingId] ?? '';
    setSubmitting(true);
    try {
      await onReview({
        findingId: row.identityFindingId,
        expectedVersion: row.identityFindingVersion,
        action,
        reapEntityId: action === 'confirm_candidate' ? candidateId : undefined,
        assignedTo: action === 'assign' ? assignedTo.trim() : undefined,
        reason: reason.trim()
      });
      setExpandedId(null);
      setReason('');
      setCandidateIds({});
      setAssignedTo('');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCreateCI = async () => {
    if (!createRequest || !onCreateCI) return;
    setCreatingCI(true);
    try {
      const result = await onCreateCI(createRequest);
      setCreateResults((current) => ({ ...current, [createRequest.deviceId]: result }));
      setLatestCreateResult(result);
      setCreateRequest(null);
    } catch {
      // The page renders the API error and keeps this request open so a retry reuses its operation ID.
    } finally {
      setCreatingCI(false);
    }
  };

  if (!data && loading) return <p>Building the REAP and CMDB comparison…</p>;
  if (!data) return <p>No synchronized network CI evidence is available yet.</p>;

  const summary = data.summary;
  return (
    <div className="cmdb-crosswalk">
      <header className="cmdb-crosswalk__header">
        <div>
          <p className="cmdb-crosswalk__eyebrow">Device identity reconciliation</p>
          <h3>Compare what REAP discovered with what the CMDB declares</h3>
          <p>Site, serial number, hostname and management IP are match evidence. Site does not restrict matching when stronger identity evidence exists.</p>
        </div>
        <button type="button" className="shortcut-button" disabled={loading} onClick={onRefresh}>{loading ? 'Refreshing…' : 'Refresh comparison'}</button>
      </header>

      <div className="cmdb-crosswalk__totals" aria-label="Crosswalk summary">
        <Summary label="REAP devices" value={summary.reapDeviceCount} />
        <Summary label="CMDB network CIs" value={summary.cmdbNetworkCICount} />
        <Summary label="Matched" value={summary.matchedCount} tone="good" />
        <Summary label="Needs review" value={summary.reviewDecisionCount} tone="warn" />
        <Summary label="REAP only" value={summary.reapOnlyCount} />
        <Summary label="CMDB only" value={summary.cmdbOnlyCount} />
      </div>
      {latestCreateResult && <CreateResult result={latestCreateResult} />}
      {summary.duplicateGroupCount > 0 && <p className="cmdb-crosswalk__notice">{summary.duplicateGroupCount} confirmed duplicate mapping {summary.duplicateGroupCount === 1 ? 'group is' : 'groups are'} shown separately because no active review action exists for them.</p>}

      <nav className="cmdb-crosswalk__filters" aria-label="Crosswalk filters">
        {filterLabels.map(([id, label]) => <button key={id} type="button" className={activeFilter === id ? 'is-active' : ''} onClick={() => changeFilter(id)}>{label}</button>)}
      </nav>

      <div className="cmdb-crosswalk__columns" aria-hidden="true"><span>REAP discovery</span><span>Identity decision</span><span>CMDB evidence</span></div>
      <div className="cmdb-crosswalk__rows">
        {groups.map((group, index) => {
          const row = group.rows[0];
          const duplicate = group.status === 'duplicate';
          const probable = group.status === 'probable';
          const tie = summarizeTopCandidateTie(row);
          const multipleCIs = group.rows.length > 1;
          const potentialDuplicate = probable && multipleCIs;
          const expanded = expandedId === group.id;
          return <article className={`cmdb-crosswalk__row cmdb-crosswalk__row--${group.status}`} key={group.id}>
            <DeviceSide
              title={row.reapDevice?.name}
              details={[row.reapDevice?.serialNumber && `Serial ${row.reapDevice.serialNumber}`, row.reapDevice?.macAddress && `MAC ${row.reapDevice.macAddress}`, row.reapDevice?.managementIp, row.reapDevice?.siteName && `Site ${row.reapDevice.siteName}`]}
              empty="No REAP candidate"
            />
            <button type="button" className="cmdb-crosswalk__decision" onClick={() => {
              setExpandedId(expanded ? null : group.id);
              setCandidateIds(Object.fromEntries(group.rows.flatMap((groupRow) => groupRow.identityFindingId ? [[groupRow.identityFindingId, groupRow.candidates[0]?.reapEntityId ?? '']] : [])));
            }} aria-expanded={expanded}>
              <strong>{`Row ${index + 1} · `}{duplicate ? 'Duplicate mapping' : potentialDuplicate ? 'Potential duplicate' : tie.tied ? 'Tied top match' : row.requiresDecision && row.status === 'cmdb_only' ? 'Not found in REAP' : group.status.replaceAll('_', ' ')}</strong>
              <span>{duplicate
                ? `${group.rows.length} CMDB CIs → 1 REAP device`
                : potentialDuplicate
                  ? `${group.rows.length} CMDB CIs → 1 REAP candidate`
                  : tie.tied
                    ? `${tie.count} REAP candidates tied at ${tie.score ?? 0}%`
                  : probable
                    ? <span className={getConfidenceBadgeClass(row.candidates[0]?.score ?? null)}>{formatConfidenceBadgeLabel(row.candidates[0]?.score ?? null, 'candidate score')}</span>
                    : row.match
                      ? <><span className={getConfidenceBadgeClass(row.match.confidence)}>{formatConfidenceBadgeLabel(row.match.confidence)}</span>{` · ${row.match.decisionMethod.replaceAll('_', ' ')}`}</>
                      : 'View evidence'}</span>
            </button>
            {multipleCIs
              ? <div className="cmdb-crosswalk__duplicate-cis" aria-label={`${group.rows.length} CMDB CIs for one REAP device`}>{group.rows.map((groupRow, index) => <CMDBSide key={groupRow.cmdbCI?.sourceIdentityId ?? groupRow.id} row={groupRow} index={index + 1} />)}</div>
              : <CMDBSide
                row={row}
                action={canCreateCI && onCreateCI && group.status === 'reap_only' && row.reapDevice?.id
                  ? <><button type="button" className="shortcut-button cmdb-crosswalk__create-button" onClick={() => setCreateRequest({ deviceId: row.reapDevice!.id, deviceName: row.reapDevice?.name ?? row.reapDevice!.id, operationId: crypto.randomUUID() })}>Create CI</button>{createResults[row.reapDevice.id] && <CreateResult result={createResults[row.reapDevice.id]} />}</>
                  : undefined}
              />}
            {expanded && <div className="cmdb-crosswalk__review">
              <p>{duplicate ? `${group.rows.length} distinct CMDB CI records are confirmed against this REAP device. Review their CI IDs and retain only the authoritative mapping.` : potentialDuplicate ? `${group.rows.length} CMDB CI records independently identify the same probable REAP device. Confirm only the authoritative CI and reject or quarantine the others.` : tie.tied ? `More than one REAP device has the same best score for this CMDB CI. Choose the authoritative device manually because there is no unique best match.` : row.explanation}</p>
              {tie.tied && <div className="cmdb-crosswalk__duplicate-evidence">{row.candidates.filter((candidate) => candidate.score === tie.score).map((candidate) => <div key={candidate.reapEntityId}><strong>{candidate.displayName || candidate.reapEntityId}</strong><span>{formatConfidenceBadgeLabel(candidate.score, 'candidate score')}</span></div>)}</div>}
              {multipleCIs
                ? <div className="cmdb-crosswalk__duplicate-evidence">{group.rows.map((groupRow) => <div key={groupRow.cmdbCI?.sourceIdentityId ?? groupRow.id}><strong>{groupRow.cmdbCI?.name ?? groupRow.cmdbCI?.externalId}</strong><span>CI ID {groupRow.cmdbCI?.externalId}</span><span>{groupRow.cmdbCI?.serialNumber ? `Serial ${groupRow.cmdbCI.serialNumber}` : 'Serial not provided'}</span>{groupRow.match ? <span className={getConfidenceBadgeClass(groupRow.match.confidence)}>{formatConfidenceBadgeLabel(groupRow.match.confidence)}</span> : <span className={getConfidenceBadgeClass(groupRow.candidates[0]?.score ?? null)}>{formatConfidenceBadgeLabel(groupRow.candidates[0]?.score ?? null, 'candidate score')}</span>}</div>)}</div>
                : (row.match?.evidence ?? row.candidates[0]?.evidence ?? []).length > 0 && <ul>{(row.match?.evidence ?? row.candidates[0]?.evidence ?? []).map((item, index) => <li key={`${item.type}-${index}`}><strong>{item.type}</strong>: {item.result} ({item.sourceValue || 'unknown'} ↔ {item.candidateValue || 'unknown'})</li>)}</ul>}
              {row.identityFindingId && <div className="cmdb-crosswalk__review-form">
                <label><span>Audit reason</span><input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this identity decision correct?" /></label>
                {group.rows.some((reviewRow) => reviewRow.allowedActions.includes('assign')) && <label><span>Assign reviewer</span><input value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)} placeholder="User or review group" /></label>}
                {group.rows.map((reviewRow) => {
                  const findingId = reviewRow.identityFindingId;
                  if (!findingId) return null;
                  const selectedCandidateId = candidateIds[findingId] ?? '';
                  return <div className="cmdb-crosswalk__review-target" key={findingId}>
                    <strong>{reviewRow.cmdbCI?.name} · CI ID {reviewRow.cmdbCI?.externalId}{reviewRow.cmdbCI?.serialNumber ? ` · Serial ${reviewRow.cmdbCI.serialNumber}` : ''}</strong>
                    {reviewRow.candidates.length > 0 && <label><span>REAP candidate</span><select value={selectedCandidateId} onChange={(event) => setCandidateIds((current) => ({ ...current, [findingId]: event.target.value }))}>{reviewRow.candidates.map((candidate) => <option key={candidate.reapEntityId} value={candidate.reapEntityId}>{candidate.displayName || candidate.reapEntityId} · score {candidate.score}</option>)}</select></label>}
                    <div>{reviewRow.allowedActions.map((action) => <button key={action} type="button" className="shortcut-button" disabled={submitting || !reason.trim() || (action === 'confirm_candidate' && !selectedCandidateId) || (action === 'assign' && !assignedTo.trim())} onClick={() => void submit(reviewRow, action)}>{action.replaceAll('_', ' ')}</button>)}</div>
                  </div>;
                })}
              </div>}
            </div>}
          </article>;
        })}
        {groups.length === 0 && <p className="cmdb-crosswalk__empty">No devices match this filter.</p>}
      </div>
      <DeleteConfirmationModal
        open={createRequest != null}
        title="Create ServiceNow CI"
        description={`Create a ServiceNow CI for ${createRequest?.deviceName ?? 'this REAP device'}?`}
        deleting={creatingCI}
        confirmLabel="Create CI"
        deletingLabel="Creating CI…"
        onCancel={() => setCreateRequest(null)}
        onConfirm={() => void confirmCreateCI()}
      />
    </div>
  );
};

const Summary = ({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'warn' }) => <div className={tone ? `cmdb-crosswalk__summary cmdb-crosswalk__summary--${tone}` : 'cmdb-crosswalk__summary'}><span>{label}</span><strong>{value}</strong></div>;

const DeviceSide = ({ title, details, empty, action, className }: { title?: string; details: Array<string | undefined>; empty: string; action?: ReactNode; className?: string }) => <div className={`cmdb-crosswalk__side${className ? ` ${className}` : ''}`}><strong>{title || empty}</strong>{title && <div>{details.filter(Boolean).map((detail) => <span key={detail}>{detail}</span>)}</div>}{action && <div className="cmdb-crosswalk__side-action">{action}</div>}</div>;

const CreateResult = ({ result }: { result: IntegrationConnectionCMDBWritebackResult }) => <p className="cmdb-crosswalk__create-result" role="status"><strong>{result.status}</strong>{result.message && ` · ${result.message}`}{result.externalCiId && ` · ServiceNow CI ${result.externalCiId}`}{result.pendingAction && <> · <strong>Next step:</strong> {result.pendingAction}</>}</p>;

const CMDBSide = ({ row, index, action }: { row: CMDBDeviceCrosswalkRow; index?: number; action?: ReactNode }) => <DeviceSide
  title={row.cmdbCI?.name}
  details={[
    index ? `CI ${index}` : undefined,
    row.cmdbCI?.externalId && `CI ID ${row.cmdbCI.externalId}`,
    row.cmdbCI?.nativeType,
    row.cmdbCI?.serialNumber && `Serial ${row.cmdbCI.serialNumber}`,
    row.cmdbCI?.macAddress && `MAC ${row.cmdbCI.macAddress}`,
    row.cmdbCI?.ipAddress,
    row.cmdbCI?.locationName && `Location ${row.cmdbCI.locationName}`
  ]}
  empty="No mapped CMDB CI"
  action={action}
  className="cmdb-crosswalk__side--cmdb"
/>;

export const groupCrosswalkRows = (rows: CMDBDeviceCrosswalkRow[]): CrosswalkGroup[] => {
  const groups: CrosswalkGroup[] = [];
  const groupedByREAPDevice = new Map<string, CrosswalkGroup>();
  for (const row of rows) {
    const groupKey = row.decisionGroupId || ((row.status === 'duplicate' || row.status === 'probable') && row.reapDevice?.id ? `${row.status}:${row.reapDevice.id}` : '');
    if (!groupKey) {
      groups.push({ id: row.id, status: row.status, rows: [row] });
      continue;
    }
    const existing = groupedByREAPDevice.get(groupKey);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    const group = { id: groupKey, status: row.status, rows: [row] };
    groupedByREAPDevice.set(groupKey, group);
    groups.push(group);
  }
  return groups;
};

const summarizeTopCandidateTie = (row: CMDBDeviceCrosswalkRow): TieSummary => {
  if (row.status !== 'ambiguous' || row.candidates.length < 2) {
    return { tied: false, count: 0, score: null };
  }
  const scores = row.candidates.map((candidate) => candidate.score).filter((score): score is number => typeof score === 'number');
  if (scores.length < 2) {
    return { tied: false, count: 0, score: null };
  }
  const bestScore = Math.max(...scores);
  const count = row.candidates.filter((candidate) => candidate.score === bestScore).length;
  if (count < 2) {
    return { tied: false, count: 0, score: null };
  }
  return { tied: true, count, score: bestScore };
};

export default CMDBDeviceCrosswalk;
